import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Cpu, Database, Network, HardDrive, Server, Users, AlertTriangle, CheckCircle,
  BarChart3, Clock, RotateCcw, Timer,
} from 'lucide-react';
import { monitoringApi, awsApi } from '../services/api';
import { Card, SectionHeader, Loader, StatCard, Badge, Button } from '../components/ui';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, AreaChart, Area,
} from 'recharts';

const LAYERS = [
  { id: 'COMPUTE',     label: 'Compute',     icon: Cpu,       color: '#0084ff' },
  { id: 'DATABASE',    label: 'Database',     icon: Database,  color: '#a78bfa' },
  { id: 'NETWORK',     label: 'Network',      icon: Network,   color: '#00c896' },
  { id: 'STORAGE',     label: 'Storage',      icon: HardDrive, color: '#ffb800' },
  { id: 'APPLICATION', label: 'Application',  icon: Server,    color: '#fb923c' },
  { id: 'USER',        label: 'User',         icon: Users,     color: '#34d399' },
];

const TIME_RANGES = [
  { value: 1,    label: '1h' },
  { value: 6,    label: '6h' },
  { value: 24,   label: '24h' },
  { value: 168,  label: '7d' },
  { value: 720,  label: '30d' },
];

const CLOUD_PROVIDERS = ['All', 'AWS', 'Azure', 'GCP'];
const ENVIRONMENTS = ['All', 'production', 'staging', 'dev'];

const TABS = ['AWS Live', 'Overview', 'Database', 'Deployments'];

const tooltipStyle = {
  background: '#0d1527',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8,
  boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
};

// Mock database metrics
const MOCK_DB_METRICS = {
  connectionPool: { active: 42, idle: 18, max: 100 },
  queryLatency: [
    { time: '10:00', p50: 12, p95: 45, p99: 120 },
    { time: '10:05', p50: 14, p95: 52, p99: 135 },
    { time: '10:10', p50: 11, p95: 40, p99: 98 },
    { time: '10:15', p50: 13, p95: 48, p99: 110 },
    { time: '10:20', p50: 15, p95: 55, p99: 142 },
    { time: '10:25', p50: 12, p95: 43, p99: 105 },
    { time: '10:30', p50: 10, p95: 38, p99: 92 },
    { time: '10:35', p50: 16, p95: 58, p99: 150 },
  ],
  slowQueries: [
    { query: 'SELECT * FROM orders JOIN users ON...', avgMs: 2340, calls: 145, db: 'prod-orders' },
    { query: 'UPDATE inventory SET stock = stock -...', avgMs: 1890, calls: 89, db: 'prod-inventory' },
    { query: 'SELECT COUNT(*) FROM analytics WHERE...', avgMs: 1560, calls: 234, db: 'prod-analytics' },
    { query: 'INSERT INTO audit_log (event, user...', avgMs: 1230, calls: 1205, db: 'prod-audit' },
    { query: 'DELETE FROM sessions WHERE expired...', avgMs: 980, calls: 67, db: 'prod-sessions' },
    { query: 'SELECT p.*, c.name FROM products p...', avgMs: 870, calls: 445, db: 'prod-catalog' },
    { query: 'UPDATE users SET last_login = NOW()...', avgMs: 750, calls: 3201, db: 'prod-users' },
    { query: 'SELECT SUM(amount) FROM transactions...', avgMs: 680, calls: 56, db: 'prod-billing' },
    { query: 'INSERT INTO notifications (user_id...', avgMs: 520, calls: 890, db: 'prod-notify' },
    { query: 'SELECT * FROM cache_entries WHERE...', avgMs: 410, calls: 12034, db: 'prod-cache' },
  ],
  replication: { status: 'HEALTHY', lag: '0.3s', replicas: 3, primaryRegion: 'us-east-1' },
};

// Mock deployment metrics
const MOCK_DEPLOYMENTS = {
  successRate: 97.2,
  totalDeployments: 142,
  rollbacks: 4,
  rollbackFrequency: [
    { week: 'W1', deploys: 32, rollbacks: 1 },
    { week: 'W2', deploys: 38, rollbacks: 0 },
    { week: 'W3', deploys: 35, rollbacks: 2 },
    { week: 'W4', deploys: 37, rollbacks: 1 },
  ],
  mttr: [
    { day: 'Mon', minutes: 12 },
    { day: 'Tue', minutes: 8 },
    { day: 'Wed', minutes: 15 },
    { day: 'Thu', minutes: 6 },
    { day: 'Fri', minutes: 10 },
    { day: 'Sat', minutes: 4 },
    { day: 'Sun', minutes: 3 },
  ],
};

const tableHeaderStyle = {
  textAlign: 'left',
  padding: '10px 12px',
  color: 'rgba(255,255,255,0.35)',
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
};

export default function MonitoringPage() {
  const [selectedLayer, setSelectedLayer] = useState('COMPUTE');
  const [hours, setHours] = useState(24);
  const [activeTab, setActiveTab] = useState('AWS Live');
  const [cloudProvider, setCloudProvider] = useState('All');
  const [environment, setEnvironment] = useState('All');

  const activeConfig = LAYERS.find((l) => l.id === selectedLayer);

  const { data: layerHealth, isLoading: loadingHealth } = useQuery({
    queryKey: ['layer-health'],
    queryFn: () => monitoringApi.getLayerHealth().then((r) => r.data),
    refetchInterval: 30000,
  });

  const { data: awsOverview, isLoading: loadingAws } = useQuery({
    queryKey: ['aws-cloudwatch-overview', hours],
    queryFn: () => awsApi.getCloudWatchOverview(hours).then((r) => r.data),
    refetchInterval: 30000,
  });

  const { data: awsActiveServices } = useQuery({
    queryKey: ['aws-active-services'],
    queryFn: () => awsApi.getActiveServices().then((r) => r.data),
    refetchInterval: 60000,
  });

  const { data: metrics, isLoading: loadingMetrics } = useQuery({
    queryKey: ['metrics', selectedLayer, hours],
    queryFn: () =>
      monitoringApi.getMetrics(selectedLayer.toLowerCase(), hours).then((r) => r.data),
    refetchInterval: 30000,
  });

  const chartData = (metrics || [])
    .slice(0, 60)
    .reverse()
    .map((m) => ({
      time: new Date(m.timestamp).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      }),
      cpu: m.cpuUtilization != null ? Number(m.cpuUtilization.toFixed(1)) : null,
      memory: m.memoryUtilization != null ? Number(m.memoryUtilization.toFixed(1)) : null,
      disk: m.diskUtilization != null ? Number(m.diskUtilization.toFixed(1)) : null,
    }));

  const healthKey = selectedLayer.toLowerCase();
  const currentHealth = layerHealth?.[healthKey];

  const selectStyle = {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: 'rgba(255,255,255,0.7)',
    borderRadius: 8,
    padding: '7px 14px',
    fontSize: 12,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };

  return (
    <div>
      <SectionHeader
        title="Infrastructure Monitoring"
        subtitle="Real-time health across all 6 monitoring layers (auto-refreshes every 30s)"
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Cloud provider filter */}
            <select value={cloudProvider} onChange={(e) => setCloudProvider(e.target.value)} style={selectStyle}>
              {CLOUD_PROVIDERS.map((p) => <option key={p} value={p}>{p === 'All' ? 'All Providers' : p}</option>)}
            </select>
            {/* Environment filter */}
            <select value={environment} onChange={(e) => setEnvironment(e.target.value)} style={selectStyle}>
              {ENVIRONMENTS.map((e) => <option key={e} value={e}>{e === 'All' ? 'All Envs' : e}</option>)}
            </select>
          </div>
        }
      />

      {/* Time Range Selector */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {TIME_RANGES.map(({ value, label }) => (
          <button
            key={value}
            onClick={() => setHours(value)}
            style={{
              padding: '6px 16px',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              border: `1px solid ${hours === value ? '#0084ff' : 'rgba(255,255,255,0.1)'}`,
              background: hours === value ? 'rgba(0,132,255,0.15)' : 'transparent',
              color: hours === value ? '#4a9eff' : 'rgba(255,255,255,0.45)',
              transition: 'all 0.15s ease',
              fontFamily: 'inherit',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tab Selector */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '10px 20px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              border: 'none',
              borderBottom: activeTab === tab ? '2px solid #0084ff' : '2px solid transparent',
              background: 'transparent',
              color: activeTab === tab ? '#fff' : 'rgba(255,255,255,0.4)',
              transition: 'all 0.15s ease',
              fontFamily: 'inherit',
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'AWS Live' && (
        <>
          {/* AWS Resource Summary KPIs */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 16,
            marginBottom: 24,
          }}>
            <StatCard
              label="Active Resources"
              value={awsOverview?.totalResources ?? '—'}
              icon={Server}
              accentColor="#ff9900"
            />
            <StatCard
              label="Avg Utilization"
              value={awsOverview?.avgUtilization != null ? `${awsOverview.avgUtilization}%` : '—'}
              accentColor={awsOverview?.avgUtilization > 85 ? '#ff5c5c' : '#4a9eff'}
            />
            <StatCard
              label="Healthy / Warning / Critical"
              value={awsOverview ? `${awsOverview.healthy} / ${awsOverview.warning} / ${awsOverview.critical}` : '—'}
              accentColor="#00c896"
              icon={CheckCircle}
            />
            <StatCard
              label="AWS Namespaces"
              value={awsActiveServices?.totalNamespaces ?? '—'}
              subValue="Active services reporting metrics"
              accentColor="#a78bfa"
            />
          </div>

          {/* Active AWS Services */}
          {awsActiveServices?.services?.length > 0 && (
            <Card style={{ marginBottom: 24 }}>
              <div style={{ color: '#fff', fontWeight: 600, fontSize: 15, marginBottom: 16 }}>
                Active AWS Services (CloudWatch Namespaces)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                {awsActiveServices.services.map((svc, i) => (
                  <div key={i} style={{
                    background: 'rgba(255,153,0,0.04)', borderRadius: 10, padding: '12px 16px',
                    border: '1px solid rgba(255,153,0,0.1)',
                  }}>
                    <div style={{ color: '#ff9900', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                      {svc.namespace.replace('AWS/', '')}
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>
                      {svc.metricCount} metrics
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, marginTop: 4, maxHeight: 40, overflow: 'hidden' }}>
                      {[...svc.metrics].slice(0, 4).join(', ')}
                      {svc.metrics.length > 4 && ` +${svc.metrics.length - 4} more`}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* AWS Resource Details */}
          {loadingAws ? <Loader /> : awsOverview?.resources?.length > 0 ? (
            <>
              {/* Group resources by layer */}
              {['COMPUTE', 'DATABASE', 'NETWORK', 'STORAGE', 'APPLICATION'].map(layer => {
                const layerResources = (awsOverview?.resources || []).filter(r => r.layer === layer);
                if (layerResources.length === 0) return null;
                const layerConfig = LAYERS.find(l => l.id === layer);
                const LayerIcon = layerConfig?.icon || Server;
                return (
                  <Card key={layer} style={{ marginBottom: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: 8,
                        background: `${layerConfig?.color || '#ff9900'}18`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <LayerIcon size={16} color={layerConfig?.color || '#ff9900'} />
                      </div>
                      <div style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>
                        {layerConfig?.label || layer} Layer ({layerResources.length} resources)
                      </div>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            {['Resource', 'Service', 'Status', 'Utilization', 'Key Metrics'].map(h => (
                              <th key={h} style={tableHeaderStyle}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {layerResources.map((r, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                              <td style={{ padding: '12px', color: '#fff', fontSize: 13, fontWeight: 500 }}>{r.name}</td>
                              <td style={{ padding: '12px', color: '#ff9900', fontSize: 12 }}>{r.type}</td>
                              <td style={{ padding: '12px' }}>
                                <span style={{
                                  padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                                  background: r.status === 'HEALTHY' ? 'rgba(0,200,150,0.15)' : r.status === 'WARNING' ? 'rgba(255,184,0,0.15)' : 'rgba(255,92,92,0.15)',
                                  color: r.status === 'HEALTHY' ? '#00c896' : r.status === 'WARNING' ? '#ffb800' : '#ff5c5c',
                                }}>
                                  {r.status}
                                </span>
                              </td>
                              <td style={{ padding: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <div style={{ flex: 1, height: 6, background: 'rgba(255,255,255,0.07)', borderRadius: 3, overflow: 'hidden', maxWidth: 80 }}>
                                    <div style={{
                                      height: '100%',
                                      width: `${Math.min(r.utilization || 0, 100)}%`,
                                      background: (r.utilization || 0) > 85 ? '#ff5c5c' : (r.utilization || 0) > 60 ? '#ffb800' : '#00c896',
                                      borderRadius: 3,
                                    }} />
                                  </div>
                                  <span style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>
                                    {(r.utilization || 0).toFixed(1)}%
                                  </span>
                                </div>
                              </td>
                              <td style={{ padding: '12px', color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
                                {r.type === 'Lambda Function' && (
                                  <span>
                                    {r.invocations?.toFixed(0) || 0} invocations, {r.avgDurationMs?.toFixed(0) || 0}ms avg,{' '}
                                    <span style={{ color: r.errorRate > 5 ? '#ff5c5c' : '#00c896' }}>{r.errorRate?.toFixed(1) || 0}% errors</span>
                                  </span>
                                )}
                                {r.type === 'API Gateway' && (
                                  <span>
                                    {r.requests?.toFixed(0) || 0} requests, {r.avgLatencyMs?.toFixed(0) || 0}ms latency,{' '}
                                    <span style={{ color: r.errors5xx > 0 ? '#ff5c5c' : '#00c896' }}>{r.errors5xx?.toFixed(0) || 0} 5xx errors</span>
                                  </span>
                                )}
                                {r.type === 'S3 Bucket' && (
                                  <span>{r.sizeMB?.toFixed(2) || 0} MB, {r.objectCount?.toFixed(0) || 0} objects</span>
                                )}
                                {r.type === 'Lightsail Instance' && (
                                  <span>
                                    CPU: <span style={{ color: r.cpuUtilization > 85 ? '#ff5c5c' : '#00c896' }}>{r.cpuUtilization?.toFixed(1) || 0}%</span>,
                                    Net In: {((r.networkInBytes || 0) / 1024).toFixed(1)} KB,
                                    Net Out: {((r.networkOutBytes || 0) / 1024).toFixed(1)} KB
                                  </span>
                                )}
                                {r.metricDetails && (
                                  <span>
                                    {r.metricDetails.slice(0, 3).map((md, j) => (
                                      <span key={j}>
                                        {md.metric}: {md.sum > 0 ? md.sum : md.average}
                                        {j < Math.min(r.metricDetails.length, 3) - 1 ? ', ' : ''}
                                      </span>
                                    ))}
                                    {r.totalCalls > 0 && (
                                      <span style={{ color: r.errorRate > 5 ? '#ff5c5c' : '#00c896' }}> ({r.errorRate}% errors)</span>
                                    )}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Time series charts for resources that have them */}
                    {layerResources.filter(r => r.timeSeries?.length > 0).map((r, i) => (
                      <div key={i} style={{ marginTop: 16 }}>
                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                          {r.name} — Time Series
                        </div>
                        <ResponsiveContainer width="100%" height={150}>
                          <AreaChart data={r.timeSeries.map(p => ({
                            time: new Date(p.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                            value: p.average || p.sum || 0,
                          }))}>
                            <defs>
                              <linearGradient id={`gradTs${i}${layer}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={layerConfig?.color || '#ff9900'} stopOpacity={0.3} />
                                <stop offset="95%" stopColor={layerConfig?.color || '#ff9900'} stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                            <XAxis dataKey="time" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={tooltipStyle} />
                            <Area type="monotone" dataKey="value" stroke={layerConfig?.color || '#ff9900'} strokeWidth={2} fill={`url(#gradTs${i}${layer})`} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    ))}
                  </Card>
                );
              })}
            </>
          ) : (
            <Card style={{ marginBottom: 24 }}>
              <div style={{ textAlign: 'center', padding: 60, color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>
                {loadingAws ? 'Loading AWS CloudWatch data...' : 'No CloudWatch metrics found. Active AWS services will appear here.'}
              </div>
            </Card>
          )}
        </>
      )}

      {activeTab === 'Overview' && (
        <>
          {/* Layer Selector Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            gap: 12,
            marginBottom: 24,
          }}>
            {LAYERS.map(({ id, label, icon: Icon, color }) => {
              const health = layerHealth?.[id.toLowerCase()];
              const isActive = selectedLayer === id;
              const cpuVal = health?.avgCpu ?? 0;
              const isHighCpu = cpuVal > 85;
              return (
                <Card
                  key={id}
                  onClick={() => setSelectedLayer(id)}
                  style={{
                    cursor: 'pointer',
                    padding: 16,
                    textAlign: 'center',
                    border: `1px solid ${isHighCpu ? '#ff5c5c' : isActive ? color : 'rgba(255,255,255,0.07)'}`,
                    background: isHighCpu ? 'rgba(255,92,92,0.06)' : isActive ? `${color}10` : 'rgba(255,255,255,0.03)',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <div style={{
                    width: 38,
                    height: 38,
                    borderRadius: 8,
                    background: `${color}18`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 8px',
                    position: 'relative',
                  }}>
                    <Icon size={18} color={color} />
                    {/* Health indicator */}
                    <span style={{
                      position: 'absolute', top: -3, right: -3,
                      width: 10, height: 10, borderRadius: '50%',
                      background: cpuVal > 85 ? '#ff5c5c' : cpuVal > 60 ? '#ffb800' : '#00c896',
                      border: '2px solid #0a0f1e',
                    }} />
                  </div>
                  <div style={{
                    color: isActive ? '#fff' : 'rgba(255,255,255,0.6)',
                    fontSize: 12,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 4,
                  }}>
                    {isHighCpu && <AlertTriangle size={11} color="#ff5c5c" />}
                    {label}
                  </div>
                  {health && (
                    <div style={{
                      color: isHighCpu ? '#ff5c5c' : color,
                      fontSize: 18,
                      fontWeight: 700,
                      marginTop: 6,
                    }}>
                      {health.avgCpu?.toFixed(0) ?? '--'}%
                    </div>
                  )}
                  {health && (
                    <div style={{
                      color: 'rgba(255,255,255,0.3)',
                      fontSize: 10,
                      marginTop: 2,
                    }}>
                      {health.totalResources ?? 0} resources
                    </div>
                  )}
                </Card>
              );
            })}
          </div>

          {/* Stats for Selected Layer */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 16,
            marginBottom: 24,
          }}>
            <StatCard
              label="Total Resources"
              value={currentHealth?.totalResources ?? '\u2014'}
              icon={activeConfig?.icon}
              accentColor={activeConfig?.color}
            />
            <StatCard
              label="Avg CPU Utilization"
              value={
                currentHealth?.avgCpu != null
                  ? `${currentHealth.avgCpu.toFixed(1)}%`
                  : '\u2014'
              }
              accentColor={currentHealth?.avgCpu > 85 ? '#ff5c5c' : activeConfig?.color}
            />
            <StatCard
              label="Underutilized (<30%)"
              value={currentHealth?.underutilized ?? '\u2014'}
              subValue="Potential rightsizing targets"
              accentColor="#ffb800"
            />
          </div>

          {/* Time Series Chart */}
          <Card>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 20,
            }}>
              <div style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>
                {activeConfig?.label} Utilization
              </div>
              <Badge label="Live" type="APPLIED" />
            </div>

            {loadingMetrics ? (
              <Loader />
            ) : chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={chartData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(255,255,255,0.05)"
                  />
                  <XAxis
                    dataKey="time"
                    tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    unit="%"
                  />
                  {/* 85% threshold line */}
                  <Tooltip contentStyle={tooltipStyle} />
                  <Line
                    type="monotone"
                    dataKey="cpu"
                    stroke={activeConfig?.color}
                    strokeWidth={2}
                    dot={false}
                    name="CPU"
                  />
                  <Line
                    type="monotone"
                    dataKey="memory"
                    stroke="#a78bfa"
                    strokeWidth={2}
                    dot={false}
                    name="Memory"
                  />
                  <Line
                    type="monotone"
                    dataKey="disk"
                    stroke="#ffb800"
                    strokeWidth={2}
                    dot={false}
                    name="Disk"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: 70,
                color: 'rgba(255,255,255,0.25)',
                fontSize: 14,
              }}>
                No metrics data yet. Connect your cloud provider to stream live data.
              </div>
            )}
          </Card>
        </>
      )}

      {activeTab === 'Database' && (
        <>
          {/* Connection Pool Gauge */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 16,
            marginBottom: 24,
          }}>
            <StatCard label="Active Connections" value={MOCK_DB_METRICS.connectionPool.active} subValue={`of ${MOCK_DB_METRICS.connectionPool.max} max`} accentColor="#0084ff" icon={Database} />
            <StatCard label="Idle Connections" value={MOCK_DB_METRICS.connectionPool.idle} accentColor="#ffb800" />
            <StatCard label="Replication Status" value={MOCK_DB_METRICS.replication.status} subValue={`Lag: ${MOCK_DB_METRICS.replication.lag}`} accentColor="#00c896" icon={CheckCircle} />
            <StatCard label="Replicas" value={MOCK_DB_METRICS.replication.replicas} subValue={`Primary: ${MOCK_DB_METRICS.replication.primaryRegion}`} accentColor="#a78bfa" />
          </div>

          {/* Connection Pool Bar */}
          <Card style={{ marginBottom: 24 }}>
            <div style={{ color: '#fff', fontWeight: 600, fontSize: 15, marginBottom: 16 }}>Connection Pool Usage</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, width: 100 }}>Pool Usage</span>
              <div style={{ flex: 1, height: 24, background: 'rgba(255,255,255,0.07)', borderRadius: 6, overflow: 'hidden', position: 'relative' }}>
                <div style={{
                  height: '100%',
                  width: `${(MOCK_DB_METRICS.connectionPool.active / MOCK_DB_METRICS.connectionPool.max) * 100}%`,
                  background: 'linear-gradient(90deg, #0084ff, #00c896)',
                  borderRadius: 6,
                  transition: 'width 0.5s ease',
                }} />
                <span style={{
                  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
                  color: '#fff', fontSize: 11, fontWeight: 600,
                }}>
                  {MOCK_DB_METRICS.connectionPool.active}/{MOCK_DB_METRICS.connectionPool.max}
                </span>
              </div>
            </div>
          </Card>

          {/* Query Latency Chart with P50/P95/P99 */}
          <Card style={{ marginBottom: 24 }}>
            <div style={{ color: '#fff', fontWeight: 600, fontSize: 15, marginBottom: 16 }}>Query Latency (P50 / P95 / P99)</div>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={MOCK_DB_METRICS.queryLatency}>
                <defs>
                  <linearGradient id="gradP99" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ff5c5c" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#ff5c5c" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradP95" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ffb800" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#ffb800" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="time" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} unit="ms" />
                <Tooltip contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="p99" stroke="#ff5c5c" fill="url(#gradP99)" strokeWidth={2} name="P99" />
                <Area type="monotone" dataKey="p95" stroke="#ffb800" fill="url(#gradP95)" strokeWidth={2} name="P95" />
                <Line type="monotone" dataKey="p50" stroke="#00c896" strokeWidth={2} dot={false} name="P50" />
              </AreaChart>
            </ResponsiveContainer>
          </Card>

          {/* Top 10 Slow Queries Table */}
          <Card>
            <div style={{ color: '#fff', fontWeight: 600, fontSize: 15, marginBottom: 16 }}>Top 10 Slow Queries</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['#', 'Query', 'Avg Latency', 'Calls', 'Database'].map((h) => (
                      <th key={h} style={tableHeaderStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {MOCK_DB_METRICS.slowQueries.map((sq, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>{i + 1}</td>
                      <td style={{
                        padding: '10px 12px', color: 'rgba(255,255,255,0.7)', fontSize: 12,
                        maxWidth: 350, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        fontFamily: 'monospace',
                      }}>
                        {sq.query}
                      </td>
                      <td style={{ padding: '10px 12px', color: sq.avgMs > 1000 ? '#ff5c5c' : '#ffb800', fontSize: 13, fontWeight: 600 }}>
                        {sq.avgMs}ms
                      </td>
                      <td style={{ padding: '10px 12px', color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
                        {sq.calls.toLocaleString()}
                      </td>
                      <td style={{ padding: '10px 12px', color: '#4a9eff', fontSize: 12 }}>{sq.db}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {activeTab === 'Deployments' && (
        <>
          {/* Deployment Stats */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 16,
            marginBottom: 24,
          }}>
            <StatCard
              label="Deployment Success Rate"
              value={`${MOCK_DEPLOYMENTS.successRate}%`}
              subValue={`${MOCK_DEPLOYMENTS.totalDeployments} total this month`}
              accentColor="#00c896"
              icon={CheckCircle}
            />
            <StatCard
              label="Rollbacks"
              value={MOCK_DEPLOYMENTS.rollbacks}
              subValue="This month"
              accentColor="#ff5c5c"
              icon={RotateCcw}
            />
            <StatCard
              label="Avg MTTR"
              value="8.3 min"
              subValue="Mean time to recovery"
              accentColor="#ffb800"
              icon={Timer}
            />
          </div>

          {/* Rollback Frequency Chart */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            <Card>
              <div style={{ color: '#fff', fontWeight: 600, fontSize: 15, marginBottom: 16 }}>Deployments & Rollbacks</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={MOCK_DEPLOYMENTS.rollbackFrequency}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="week" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="deploys" fill="#0084ff" radius={[4, 4, 0, 0]} name="Deployments" />
                  <Bar dataKey="rollbacks" fill="#ff5c5c" radius={[4, 4, 0, 0]} name="Rollbacks" />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            {/* MTTR Chart */}
            <Card>
              <div style={{ color: '#fff', fontWeight: 600, fontSize: 15, marginBottom: 16 }}>MTTR Trend (minutes)</div>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={MOCK_DEPLOYMENTS.mttr}>
                  <defs>
                    <linearGradient id="gradMttr" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ffb800" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#ffb800" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="day" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} unit="m" />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Area type="monotone" dataKey="minutes" stroke="#ffb800" fill="url(#gradMttr)" strokeWidth={2} name="MTTR" />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
